import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, EyeOff } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LOGO } from "@/lib/brand";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await register(form.name, form.email, form.password);
      nav("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="relative z-10 min-h-screen flex items-center justify-center px-6 pt-24 pb-16">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="w-full max-w-md">
        <div className="text-center mb-10">
          <img src={LOGO} alt="" className="h-20 w-20 mx-auto mb-4 object-contain" />
          <h1 className="font-serif text-5xl font-light" data-testid="register-title">Crea il tuo incanto</h1>
          <p className="text-muted-foreground mt-2">Unisciti a Incanti d'Estate</p>
        </div>
        <form onSubmit={submit} className="space-y-5" data-testid="register-form">
          <div><Label>Nome</Label><Input required name="name" autoComplete="name" value={form.name} onChange={set("name")} className="mt-2" data-testid="register-name" /></div>
          <div><Label>Email</Label><Input type="email" required name="email" autoComplete="email" value={form.email} onChange={set("email")} className="mt-2" data-testid="register-email" /></div>
          <div><Label>Password</Label>
            <div className="relative mt-2">
              <Input type={showPassword ? "text" : "password"} required minLength={6} name="password" autoComplete="new-password" value={form.password} onChange={set("password")} className="pr-11" data-testid="register-password" />
              <button type="button" onClick={() => setShowPassword((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-gold transition-colors" aria-label={showPassword ? "Nascondi password" : "Mostra password"} data-testid="toggle-password-visibility">
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          {error && <p className="text-sm text-destructive" data-testid="register-error">{error}</p>}
          <Button type="submit" disabled={loading} className="w-full rounded-full bg-gold hover:bg-gold/90 text-white h-12" data-testid="register-submit">
            {loading ? "Creazione…" : "Registrati"}
          </Button>
        </form>
        <p className="text-center text-sm text-muted-foreground mt-8">
          Hai già un account? <Link to="/login" className="text-gold hover:underline" data-testid="go-login">Accedi</Link>
        </p>
      </motion.div>
    </div>
  );
}
